/**
 * 全站表格：含操作按钮的列按按钮文字实际宽度自动加宽（只加宽、不缩窄）。
 * 按钮在单元格内按「视觉行」统计：同一行按钮宽度相加；网格布局按列数 × 最宽按钮计算。
 * 表格根节点加 class「no-btn-autofit」可关闭。
 * 另：CSS max-height 产生的纵向滚动条同步给 Element 布局，预留滚动条宽度。
 * 创建于 2026-09-27
 * 最后更新：2026-09-28
 */

const BUTTON_SELECTOR = '.el-button, .op-link, .table-op-link, .sc-link-btn'
/** 每列最多采样的单元格数，避免大表格测量开销 */
const MAX_SAMPLE_CELLS = 60
const SCHEDULE_DELAY = 80

function px(value) {
  const n = parseFloat(value)
  return isNaN(n) ? 0 : n
}

/** 按钮完整显示所需宽度：内容宽 + 左右内边距 + 边框 */
function measureButtonNeed(btn) {
  const range = document.createRange()
  range.selectNodeContents(btn)
  const contentWidth = range.getBoundingClientRect().width
  if (!contentWidth) return 0
  const cs = window.getComputedStyle(btn)
  return contentWidth +
    px(cs.paddingLeft) + px(cs.paddingRight) +
    px(cs.borderLeftWidth) + px(cs.borderRightWidth)
}

/** 单元格完整显示所有按钮所需宽度 */
function measureCellNeed(td) {
  const cell = td.querySelector('.cell')
  if (!cell) return 0
  const buttons = cell.querySelectorAll(BUTTON_SELECTOR)
  if (!buttons.length) return 0

  const rowMap = new Map()
  buttons.forEach((btn) => {
    if (btn.parentElement && btn.parentElement.closest(BUTTON_SELECTOR)) return
    const rect = btn.getBoundingClientRect()
    if (!rect.height) return
    const need = measureButtonNeed(btn)
    if (!need) return
    const key = Math.round(rect.top)
    let row = rowMap.get(key)
    if (!row) {
      row = { parent: btn.parentElement, items: [] }
      rowMap.set(key, row)
    }
    row.items.push({ btn, need })
  })

  let maxRow = 0
  rowMap.forEach((row) => {
    const parentStyle = window.getComputedStyle(row.parent)
    const columnGap = px(parentStyle.columnGap)
    const maxItem = Math.max(...row.items.map((it) => it.need))
    let rowNeed = 0
    if (parentStyle.display === 'grid' || parentStyle.display === 'inline-grid') {
      const colCount = Math.max(1, parentStyle.gridTemplateColumns.split(' ').filter(Boolean).length)
      rowNeed = colCount * maxItem + (colCount - 1) * columnGap
    } else if (row.items.length > 1 && row.items.some((it) => window.getComputedStyle(it.btn).maxWidth.includes('%'))) {
      rowNeed = row.items.length * maxItem + (row.items.length - 1) * Math.max(8, columnGap)
    } else {
      row.items.forEach((it, idx) => {
        rowNeed += it.need
        if (idx > 0) {
          rowNeed += Math.max(4, columnGap + px(window.getComputedStyle(it.btn).marginLeft))
        }
      })
    }
    if (rowNeed > maxRow) maxRow = rowNeed
  })
  if (!maxRow) return 0

  const cellStyle = window.getComputedStyle(cell)
  return maxRow + px(cellStyle.paddingLeft) + px(cellStyle.paddingRight)
}

/**
 * 测量并加宽当前表格中放不下按钮的列
 * @param {Vue} table ElTable 实例
 */
function fitButtonColumns(table) {
  const el = table.$el
  if (!el || !el.offsetWidth || el.classList.contains('no-btn-autofit')) return
  const columns = (table.store && table.store.states && table.store.states.columns) || []
  let changed = false

  columns.forEach((column) => {
    if (!column || !column.id || ['selection', 'index', 'expand'].includes(column.type)) return
    const tds = el.querySelectorAll(`.el-table__body td.${column.id}`)
    if (!tds.length) return

    let need = 0
    const count = Math.min(tds.length, MAX_SAMPLE_CELLS)
    for (let i = 0; i < count; i++) {
      const cellNeed = measureCellNeed(tds[i])
      if (cellNeed > need) need = cellNeed
    }
    if (!need) return

    need = Math.ceil(need) + 2
    const current = column.realWidth || column.width || column.minWidth || 0
    if (need <= current) return

    if (typeof column.width === 'number') {
      column.width = need
    } else {
      column.minWidth = need
    }
    changed = true
  })

  if (changed) table.doLayout()
}

/**
 * 未设 height、由 CSS max-height 产生纵向滚动的表格（如弹窗内表格）：
 * Element 不检测这种滚动，不预留滚动条宽度，滚动条会盖住最后一列。此处按实际滚动状态同步。
 * @param {Vue} table ElTable 实例
 */
function syncCssScrollY(table) {
  const layout = table.layout
  const bodyWrapper = table.bodyWrapper
  if (!layout || !bodyWrapper || layout.height !== null || !bodyWrapper.offsetWidth) return
  const hasScrollY = bodyWrapper.scrollHeight > bodyWrapper.clientHeight + 1 &&
    bodyWrapper.offsetWidth - bodyWrapper.clientWidth > 0
  if (hasScrollY === !!layout.scrollY) return
  layout.scrollY = hasScrollY
  layout.notifyObservers('scrollable')
  layout.updateColumnsWidth()
}

const autoFitMixin = {
  created() {
    const rawResizeListener = this.resizeListener
    if (typeof rawResizeListener === 'function') {
      this.resizeListener = (...args) => {
        rawResizeListener.apply(this, args)
        this.scheduleButtonColumnFit()
      }
    }
  },
  mounted() {
    this.scheduleButtonColumnFit()
  },
  watch: {
    data() {
      this.scheduleButtonColumnFit()
    }
  },
  beforeDestroy() {
    clearTimeout(this.buttonColumnFitTimer)
  },
  methods: {
    /** 延迟到渲染完成后再测量，合并短时间内的多次触发 */
    scheduleButtonColumnFit() {
      clearTimeout(this.buttonColumnFitTimer)
      this.buttonColumnFitTimer = setTimeout(() => {
        this.$nextTick(() => {
          try {
            syncCssScrollY(this)
            fitButtonColumns(this)
          } catch (e) {
            /* 测量失败不影响表格正常使用 */
          }
        })
      }, SCHEDULE_DELAY)
    }
  }
}

/**
 * 为 ElTable 注入按钮列自动加宽能力，须在 Vue.use(ElementUI) 之前调用
 * @param {*} ElementUI
 */
export function installTableButtonColumnAutoFit(ElementUI) {
  const Table = ElementUI && ElementUI.Table
  if (!Table) return
  Table.mixins = (Table.mixins || []).concat(autoFitMixin)
}
